import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface Reseller {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  status: string;
  commissionRate: number;
  clientCount: number;
  totalEarnings: number;
  pendingEarnings: number;
  createdAt: string;
}

interface CommissionRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  reseller: Reseller | null;
  onSuccess: () => void;
}

const CommissionRateModal: React.FC<CommissionRateModalProps> = ({ isOpen, onClose, reseller, onSuccess }) => {
  const [commissionRate, setCommissionRate] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen && reseller) {
      setCommissionRate(reseller.commissionRate);
      setError('');
    }
  }, [isOpen, reseller]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reseller) return;

    setLoading(true);
    setError('');

    const response = await api.updateResellerCommissionRate(reseller.id, commissionRate);

    if (response.success) {
      onSuccess();
      onClose();
    } else {
      setError(response.error || 'Failed to update commission rate');
    }

    setLoading(false);
  };

  if (!isOpen || !reseller) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#262626]">
          <h3 className="text-lg font-bold text-white">Update Commission Rate</h3>
          <p className="text-neutral-500 text-sm">{reseller.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-neutral-400 text-sm mb-2">Current Rate: {reseller.commissionRate}%</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="50"
                step="0.5"
                value={commissionRate}
                onChange={(e) => setCommissionRate(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-[#262626] rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="w-20 relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
                  className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-3 py-2 text-white text-center focus:outline-none focus:border-emerald-500/50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">%</span>
              </div>
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex gap-2 flex-wrap">
            {[5, 10, 15, 20, 25, 30].map((rate) => (
              <button
                key={rate}
                type="button"
                onClick={() => setCommissionRate(rate)}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  commissionRate === rate
                    ? 'bg-emerald-500 text-white'
                    : 'bg-[#1a1a1a] text-neutral-400 hover:bg-[#262626]'
                }`}
              >
                {rate}%
              </button>
            ))}
          </div>

          {/* Earnings Preview */}
          <div className="p-4 bg-[#1a1a1a] border border-[#262626] rounded-xl">
            <p className="text-neutral-400 text-sm mb-2">Earnings Preview (on ৳10,000 sale)</p>
            <div className="flex items-center justify-between">
              <span className="text-neutral-500">Reseller earns:</span>
              <span className="text-emerald-400 font-bold">৳{(10000 * commissionRate / 100).toLocaleString()}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-[#1a1a1a] hover:bg-[#262626] text-white rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl transition-colors"
            >
              {loading ? 'Updating...' : 'Update Rate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ResellerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resellerId: string | null;
}

const ResellerDetailsModal: React.FC<ResellerDetailsModalProps> = ({ isOpen, onClose, resellerId }) => {
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && resellerId) {
      loadDetails();
    }
  }, [isOpen, resellerId]);

  const loadDetails = async () => {
    if (!resellerId) return;
    setLoading(true);
    const response = await api.getResellerDetails(resellerId);
    if (response.success && response.data) {
      setDetails(response.data);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Reseller Details</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">✕</button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* User Info */}
              <div>
                <h4 className="text-sm font-medium text-neutral-500 uppercase mb-3">Contact Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-neutral-500 text-xs">Name</p>
                    <p className="text-white">{details.user.firstName} {details.user.lastName}</p>
                  </div>
                  <div>
                    <p className="text-neutral-500 text-xs">Email</p>
                    <p className="text-white">{details.user.email}</p>
                  </div>
                  <div>
                    <p className="text-neutral-500 text-xs">Phone</p>
                    <p className="text-white">{details.user.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-neutral-500 text-xs">Company</p>
                    <p className="text-white">{details.user.companyName || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Commission Info */}
              <div>
                <h4 className="text-sm font-medium text-neutral-500 uppercase mb-3">Commission</h4>
                <div className="p-4 bg-[#1a1a1a] border border-[#262626] rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Commission Rate</span>
                    <span className="text-emerald-400 font-bold text-lg">{details.profile?.commissionRate || 10}%</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Payout Method</span>
                    <span className="text-white">{details.profile?.payoutMethod || 'Not Set'}</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div>
                <h4 className="text-sm font-medium text-neutral-500 uppercase mb-3">Statistics</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl">
                    <p className="text-neutral-500 text-xs">Total Clients</p>
                    <p className="text-white text-xl font-bold">{details.stats.totalClients}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl">
                    <p className="text-neutral-500 text-xs">Active Clients</p>
                    <p className="text-emerald-400 text-xl font-bold">{details.stats.activeClients}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl">
                    <p className="text-neutral-500 text-xs">Total Earnings</p>
                    <p className="text-white text-xl font-bold">৳{details.stats.totalCommission.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl">
                    <p className="text-neutral-500 text-xs">Pending Payout</p>
                    <p className="text-yellow-400 text-xl font-bold">৳{details.stats.pendingCommission.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-neutral-500 text-center py-8">Failed to load details</p>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminResellers: React.FC = () => {
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReseller, setSelectedReseller] = useState<Reseller | null>(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedResellerId, setSelectedResellerId] = useState<string | null>(null);

  useEffect(() => {
    loadResellers();
  }, [page, statusFilter]);

  const loadResellers = async () => {
    setLoading(true);
    const response = await api.getResellers({
      page,
      pageSize: 10,
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    });
    if (response.success && response.data) {
      setResellers(response.data.data);
      setTotalPages(response.data.totalPages);
    }
    setLoading(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadResellers();
  };

  const handleApprove = async (id: string) => {
    const response = await api.approveUser(id);
    if (response.success) {
      loadResellers();
    } else {
      alert(response.error || 'Failed to approve');
    }
  };

  const handleSuspend = async (id: string) => {
    const reason = prompt('Suspension reason:');
    if (!reason) return;
    const response = await api.suspendUser(id, reason);
    if (response.success) {
      loadResellers();
    } else {
      alert(response.error || 'Failed to suspend');
    }
  };

  const handleEditCommission = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setShowCommissionModal(true);
  };

  const handleViewDetails = (resellerId: string) => {
    setSelectedResellerId(resellerId);
    setShowDetailsModal(true);
  };

  if (loading && resellers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading resellers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Reseller Management</h1>
          <p className="text-neutral-500">Manage reseller partners and commission rates</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or company..."
              className="w-full bg-[#121212] border border-[#262626] rounded-xl px-4 py-3 pl-10 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">🔍</span>
          </div>
        </form>
        <div className="flex gap-2">
          {['all', 'active', 'pending', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-emerald-600 text-white'
                  : 'bg-[#1a1a1a] border border-[#262626] text-neutral-400 hover:bg-[#262626]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {resellers.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Reseller</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Commission</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Clients</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Earnings</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Status</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {resellers.map((reseller) => (
                    <tr key={reseller.id} className="hover:bg-[#1a1a1a] transition-colors">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{reseller.firstName} {reseller.lastName}</p>
                          <p className="text-neutral-500 text-sm">{reseller.email}</p>
                          {reseller.companyName && (
                            <p className="text-neutral-600 text-xs">{reseller.companyName}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg font-bold">
                          {reseller.commissionRate}%
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-white font-medium">{reseller.clientCount}</p>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">৳{reseller.totalEarnings.toLocaleString()}</p>
                          {reseller.pendingEarnings > 0 && (
                            <p className="text-yellow-400 text-xs">৳{reseller.pendingEarnings.toLocaleString()} pending</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          reseller.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                          reseller.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {reseller.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewDetails(reseller.id)}
                            className="px-3 py-1 bg-[#262626] text-neutral-300 rounded-lg text-xs hover:bg-[#363636]"
                          >
                            View
                          </button>
                          {reseller.status === 'active' && (
                            <>
                              <button
                                onClick={() => handleEditCommission(reseller)}
                                className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20"
                              >
                                Commission
                              </button>
                              <button
                                onClick={() => handleSuspend(reseller.id)}
                                className="px-3 py-1 bg-red-500/10 text-red-400 rounded-lg text-xs hover:bg-red-500/20"
                              >
                                Suspend
                              </button>
                            </>
                          )}
                          {reseller.status === 'pending' && (
                            <button
                              onClick={() => handleApprove(reseller.id)}
                              className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="p-4 border-t border-[#262626] flex items-center justify-between">
                <p className="text-neutral-500 text-sm">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50">Previous</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <p className="text-neutral-500">No resellers found</p>
          </div>
        )}
      </div>

      {/* Commission Rate Modal */}
      <CommissionRateModal
        isOpen={showCommissionModal}
        onClose={() => {
          setShowCommissionModal(false);
          setSelectedReseller(null);
        }}
        reseller={selectedReseller}
        onSuccess={loadResellers}
      />

      {/* Reseller Details Modal */}
      <ResellerDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedResellerId(null);
        }}
        resellerId={selectedResellerId}
      />
    </div>
  );
};

export default AdminResellers;
